import { IVR_PROXY_BASE, normalizeIvrPhone } from '../config/ivr';

const getCookie = (name) => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      try {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      } catch {
        return c.substring(nameEQ.length, c.length);
      }
    }
  }
  return null;
};

const clearAuthAndLogin = () => {
  document.cookie = 'adminToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'adminUser=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  try {
    localStorage.removeItem('adminUser');
  } catch (_) {
    /* ignore */
  }
  window.location.href = '/login';
};

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || `HTTP ${res.status}` };
  }
}

/**
 * Call TabTrips ivr.php proxy. Never talks to ivrsolutions.in.
 */
async function ivrRequest(action, body = {}) {
  const token = getCookie('adminToken');
  try {
    const res = await fetch(`${IVR_PROXY_BASE}?action=${encodeURIComponent(action)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body || {}),
    });

    const data = await parseJson(res);

    if (res.status === 401 || data?.message?.toLowerCase?.().includes('invalid token')) {
      clearAuthAndLogin();
      return {
        success: false,
        error: data.message || 'Unauthorized',
        status: 401,
        data,
      };
    }

    if (res.status === 403 || data.status === false) {
      return {
        success: false,
        error: data.message || 'No access',
        required_feature: data.required_feature,
        status: res.status === 403 ? 403 : res.status,
        data,
      };
    }

    if (res.status === 429) {
      return {
        success: false,
        error: data.message || 'Too many calls — slow down and try again',
        status: 429,
        data,
      };
    }

    if (!res.ok && data.status !== true) {
      return {
        success: false,
        error: data.message || `Request failed (${res.status})`,
        status: res.status,
        data,
      };
    }

    if (data.status === false) {
      return {
        success: false,
        error: data.message || 'Request failed',
        required_feature: data.required_feature,
        data,
      };
    }

    return {
      success: true,
      message: data.message,
      logid: data.logid ?? data.data?.logid ?? null,
      data: data.data !== undefined ? data.data : data,
      raw: data,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Network error',
      data: null,
    };
  }
}

const asLogList = (res, logTypeTag) => {
  if (!res.success) {
    return { ...res, logs: [] };
  }
  const payload = res.data || {};
  let logs = Array.isArray(payload.logs)
    ? payload.logs
    : Array.isArray(payload)
      ? payload
      : Array.isArray(payload.data)
        ? payload.data
        : [];
  if (logTypeTag) {
    logs = logs.map((r) => ({
      ...r,
      log_type: r.log_type || logTypeTag,
      _log_source: r.log_type || logTypeTag,
    }));
  }
  return { ...res, logs };
};

/** agents + dids + default_did (scoped for limited admins) */
export const ivrMeta = async () => {
  const res = await ivrRequest('ivrMeta', {});
  if (!res.success) {
    return {
      ...res,
      agents: [],
      dids: [],
      default_did: null,
      scoped: false,
    };
  }
  const d = res.data || {};
  const agents = Array.isArray(d.agents) ? d.agents : [];
  const didList = Array.isArray(d.dids) ? d.dids : [];
  const dids = didList
    .map((x) => (typeof x === 'string' ? x : x?.did_no || x?.did_number || x?.did))
    .filter(Boolean);
  return {
    success: true,
    agents,
    dids,
    default_did: d.default_did || null,
    scoped: d.scoped === true,
    admin_phone_key: d.admin_phone_key || null,
    message: res.message,
    data: d,
  };
};

export const ivrGetAgents = async () => {
  const res = await ivrRequest('ivrGetAgents', {});
  if (!res.success) return { ...res, agents: [] };
  const d = res.data || {};
  const agents = Array.isArray(d.agents) ? d.agents : Array.isArray(d) ? d : [];
  return { success: true, agents, scoped: d.scoped === true, data: d };
};

export const ivrGetDids = async () => {
  const res = await ivrRequest('ivrGetDids', {});
  if (!res.success) return { ...res, dids: [] };
  const d = res.data || {};
  const raw = d.dids || d || [];
  const list = Array.isArray(raw) ? raw : [];
  const dids = list
    .map((x) => (typeof x === 'string' ? x : x?.did_no || x?.did_number || x?.did))
    .filter(Boolean);
  return {
    success: true,
    dids,
    default_did: d.default_did || null,
    data: d,
  };
};

export const ivrClickToCall = async ({
  did,
  ext_no,
  phone,
  context,
  booking_id,
}) => {
  const body = {
    did: String(did || '').trim(),
    ext_no: String(ext_no || '').trim(),
    phone: normalizeIvrPhone(phone),
  };
  if (context) body.context = context;
  if (booking_id != null && String(booking_id).trim() !== '') {
    body.booking_id = Number.isNaN(Number(booking_id))
      ? booking_id
      : Number(booking_id);
  }
  const res = await ivrRequest('ivrClickToCall', body);
  if (!res.success) return res;
  return {
    success: true,
    message: res.message || 'Call connecting. Please wait...',
    logid: res.logid,
    data: res.data,
  };
};

export const ivrC2cAdvance = (payload) => ivrRequest('ivrC2cAdvance', payload);

export const ivrGetCallLogs = async (params = {}) =>
  asLogList(await ivrRequest('ivrGetCallLogs', params), 'inbound');

export const ivrGetClickToCallLogs = async (params = {}) =>
  asLogList(await ivrRequest('ivrGetClickToCallLogs', params), 'c2c');

export const ivrGetDialerLogs = async (params = {}) =>
  asLogList(await ivrRequest('ivrGetDialerLogs', params), 'dialer');

export const ivrGetClickConnectLogs = async (params = {}) =>
  asLogList(await ivrRequest('ivrGetClickConnectLogs', params), 'masking');

export const ivrGetAllCallLogsRecent = async () => {
  const res = await ivrRequest('ivrGetAllCallLogsRecent', {});
  if (!res.success) return { ...res, logs: [] };
  const d = res.data || {};
  if (Array.isArray(d.logs)) {
    return { ...res, logs: d.logs };
  }
  const inbound = (d.call_logs || []).map((r) => ({ ...r, log_type: 'inbound', _log_source: 'inbound' }));
  const c2c = (d.click_to_call_logs || []).map((r) => ({ ...r, log_type: 'c2c', _log_source: 'c2c' }));
  const dialer = (d.dialer_logs || []).map((r) => ({ ...r, log_type: 'dialer', _log_source: 'dialer' }));
  const logs = [...inbound, ...c2c, ...dialer].sort((a, b) =>
    String(b.call_time || '').localeCompare(String(a.call_time || ''))
  );
  return { ...res, logs };
};

export const ivrCallLogsByNumber = async (phone) =>
  asLogList(
    await ivrRequest('ivrCallLogsByNumber', { phone: normalizeIvrPhone(phone) }),
    'inbound'
  );

export const ivrC2cLogsByNumber = async (phone) =>
  asLogList(
    await ivrRequest('ivrC2cLogsByNumber', { phone: normalizeIvrPhone(phone) }),
    'c2c'
  );

export const ivrClickConnect = (payload) => ivrRequest('ivrClickConnect', payload);
export const ivrClickConnectUpdate = (payload) => ivrRequest('ivrClickConnectUpdate', payload);

export const ivrEnableDisableAgent = (ext_no, status) =>
  ivrRequest('ivrEnableDisableAgent', {
    ext_no: String(ext_no).trim(),
    status: String(status),
  });

export const ivrAddAgent = (payload) => ivrRequest('ivrAddAgent', payload);

export const ivrUpdateAgentDid = (agent_ext, did_num) =>
  ivrRequest('ivrUpdateAgentDid', {
    agent_ext: String(agent_ext).trim(),
    did_num: String(did_num).trim(),
  });

export const ivrDialByText = (payload) => ivrRequest('ivrDialByText', payload);
